import React, { useContext, useState } from "react";
import classNames from "classnames";
import { AlertType, SalesStatus } from "../../../interfaces/enums";
import { StyledNumberInput } from "../../styled/styled-components";
import { ColorSizeStock } from "../interfaces";
import BackOrderInfo from "./BackOrderInfo/BackOrderInfo";
import OverseaStock from "./OverseaStock/OverseaStock";
import { Context, IContext } from "../../../context/ContextApp";
import { calculateStock, getLocationStock, getOverseaStock } from "./utils";
import {
  AU_LOCATIONS,
  QTY_CAPPED_STATUSES,
  BACK_ORDER_EXCLUDED_STATUSES,
  SizeStockProps,
} from "./types";
import Icon from "../../Icon/icon";
import Button, { ButtonType } from "../../Button/Button";

const SizeDetailTable = (props: SizeStockProps) => {
  const {
    shouldRenderSizeDetail,
    currentSelectedColor,
    currentColorSizeStocks,
    itemPrice,
    setActionStatus,
    product,
    setCurrentColorSizeStocks,
    inputRefs,
    setInputRefs,
  } = props;

  const { appBranch }: IContext = useContext(Context);
  const [auStocks, setAuStocks] = useState<ColorSizeStock[]>([]);
  const [caStocks, setCaStocks] = useState<ColorSizeStock[]>([]);
  const [isAUBreakdownOpen, setIsAUBreakdownOpen] = useState(false);

  const isStockListMode = localStorage.getItem("stocklist_mode") === "true";
  const isClearance = product.slug.includes("clearance");
  const isSyzmikBoots = product.brand === "syzmik-boots";
  const showBOColumn = !BACK_ORDER_EXCLUDED_STATUSES.includes(product.sales_status) && !isClearance;

  const onQuantityInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    size: ColorSizeStock,
  ) => {
    size.errorMessage = "";
    size.backOrderQty = 0;

    const stockAvailable = size.stock.reduce((sum, st) => sum + st.qtyAvailable, 0);
    let value = parseInt(event.target.value);

    if (
      (isClearance || QTY_CAPPED_STATUSES.includes(product.sales_status)) &&
      value > stockAvailable
    ) {
      value = stockAvailable;
    }

    size.quantity = value;
    if (stockAvailable < value) {
      size.backOrderQty = value - Math.max(0, stockAvailable);
    }

    const updatedSizes = currentColorSizeStocks.map((s) => (s.sku === size.sku ? size : s));
    setInputRefs({ ...inputRefs, [size.sku]: value });
    setCurrentColorSizeStocks(updatedSizes);
  };

  const renderInput = (size: ColorSizeStock) => {
    const stockAvailable = size.stock.reduce((sum, st) => sum + st.qtyAvailable, 0);
    const hasEta = size.eta && size.eta.length > 0;

    if (stockAvailable <= 0 && (!hasEta || product.sales_status === SalesStatus.Clearance)) {
      return <div>N/A</div>;
    }

    return (
      <StyledNumberInput
        className="size-input-compact"
        type="number"
        min="0"
        value={inputRefs[size.sku] ?? 0}
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          if (!e.target.value) {
            size.quantity = 0;
            size.errorMessage = "";
            e.target.value = "0";
          }
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuantityInputChange(e, size)}
      />
    );
  };

  if (!shouldRenderSizeDetail || currentColorSizeStocks.length === 0) return <></>;

  if (!itemPrice) {
    setActionStatus({
      message: "There is no price set up for this product. Please contact customer support.",
      type: AlertType.Error,
    });
    return <></>;
  }

  return (
    <table className="table table-sm size-detail-table" id="size-detail-table">
      <thead>
        <tr>
          <th>
            Size
            {isSyzmikBoots && (
              <>
                <br />
                (UK Size)
              </>
            )}
          </th>

          <th className="col-branch-stock font-weight-bold">
            <span className="text-uppercase">{appBranch}</span> Stock
            {appBranch === "au" && (
              <div className="btn-compact-wrapper">
                <Button
                  onClick={() => setIsAUBreakdownOpen(!isAUBreakdownOpen)}
                  type={ButtonType.secondary}
                  className="btn-compact"
                >
                  <span
                    style={{
                      display: "inline-flex",
                      transform: isAUBreakdownOpen ? "rotate(90deg)" : "rotate(-90deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <Icon name="ArrowDown" className="auStock-Icon" />
                  </span>
                  {isAUBreakdownOpen ? "Hide Details" : "Show Details"}
                </Button>
              </div>
            )}
          </th>
          {appBranch === "au" &&
            isAUBreakdownOpen &&
            AU_LOCATIONS.map((loc) => (
              <th key={loc.regionID} className="text-nowrap slide-in-right">
                {loc.name}
              </th>
            ))}
          {!isStockListMode && <th>Qty</th>}

          {showBOColumn && <th className="bo-header">B/O:</th>}

          {showBOColumn && <th>ETA/Qty</th>}

          {appBranch === "nz" && (
            <>
              <th>
                <div className="stock-level">
                  <OverseaStock
                    branch="au"
                    color={currentSelectedColor}
                    onStocksLoaded={setAuStocks}
                  />
                </div>
              </th>
              <th>
                <div className="stock-level">
                  <OverseaStock
                    branch="ca"
                    color={currentSelectedColor}
                    onStocksLoaded={setCaStocks}
                  />
                </div>
              </th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {currentColorSizeStocks.map((size: ColorSizeStock) => (
          <tr key={size.sku}>
            <td className="text-center">{size.size}</td>
            <td
              className={classNames({
                "font-weight-bold": appBranch === "au" && isAUBreakdownOpen,
                "font-weight-normal": !(appBranch === "au" && isAUBreakdownOpen),
              })}
            >
              {calculateStock(size)}
            </td>
            {appBranch === "au" &&
              isAUBreakdownOpen &&
              AU_LOCATIONS.map((loc) => (
                <td key={loc.regionID} className="slide-in-right">
                  {getLocationStock(size, loc.regionID)}
                </td>
              ))}
            {!isStockListMode && <td className="text-center">{renderInput(size)}</td>}
            {showBOColumn && (
              <td>
                {size.backOrderQty ? <div style={{ color: "red" }}>{size.backOrderQty}</div> : "-"}
              </td>
            )}
            {showBOColumn && (
              <td>
                <BackOrderInfo size={size} />
              </td>
            )}
            {appBranch === "nz" && (
              <>
                <td>{getOverseaStock(auStocks, size.sku)}</td>
                <td>{getOverseaStock(caStocks, size.sku)}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default SizeDetailTable;
